import { postApi } from './apiServices';

// Stripe: controlled ONLY by REACT_APP_STRIPE_PUBLISHABLE_KEY (no hardcoded fallback).
// Production: use pk_live_... in .env. Backend must use the matching live secret key (sk_live_...) in its env (e.g. STRIPE_SECRET_KEY).
// Test: use pk_test_... here and sk_test_... on the server.
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (typeof window !== 'undefined' && STRIPE_PUBLISHABLE_KEY) {
  const stripeMode = STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_') ? 'TEST' : STRIPE_PUBLISHABLE_KEY.startsWith('pk_live_') ? 'LIVE' : 'UNKNOWN';
  console.log('[Stripe] Publishable key mode:', stripeMode, '(backend must use matching secret key)');
} else if (typeof window !== 'undefined' && !STRIPE_PUBLISHABLE_KEY) {
  console.warn('[Stripe] REACT_APP_STRIPE_PUBLISHABLE_KEY is not set. Set it in .env to enable payments.');
}

/**
 * Create a payment intent on the server
 * @param {Object} paymentData - Payment data including amount and currency
 * @returns {Promise} Payment intent response
 */
export const createPaymentIntent = async (paymentData) => {
  try {
    const response = await postApi('/api/create-payment-intent', paymentData);
    return response;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

/**
 * Confirm payment with Stripe
 * @param {Object} stripe - Stripe instance
 * @param {Object} elements - Stripe elements
 * @param {string} clientSecret - Payment intent client secret
 * @returns {Promise} Payment confirmation result
 */
export const confirmPayment = async (stripe, elements, clientSecret) => {
  try {
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`,
      },
      redirect: 'if_required',
    });

    if (error) {
      throw error;
    }

    return paymentIntent;
  } catch (error) {
    console.error('Error confirming payment:', error);
    throw error;
  }
};

/**
 * Get Stripe publishable key (from REACT_APP_STRIPE_PUBLISHABLE_KEY only).
 * @returns {string|undefined} Stripe publishable key; undefined if env not set
 */
export const getStripePublishableKey = () => {
  return STRIPE_PUBLISHABLE_KEY;
};

/**
 * Format amount for Stripe (convert to cents)
 * @param {number} amount - Amount in dollars
 * @returns {number} Amount in cents
 */
export const formatAmountForStripe = (amount) => {
  return Math.round(amount * 100);
};

/**
 * Format amount from Stripe (convert from cents)
 * @param {number} amount - Amount in cents
 * @returns {number} Amount in dollars
 */
export const formatAmountFromStripe = (amount) => {
  return amount / 100;
};

/**
 * Finalize eSIM order after successful payment
 * @param {Object} orderData - Order data including payment details, billing info, and an array of cart items
 * @returns {Promise} Finalization response
 */
export const finalizeSimOrder = async (orderData) => {
  try {
    const response = await postApi('/api/finalize-sim', orderData);
    
    // Check if the API response indicates an error
    if (response && response.status === false) {
      throw new Error(response.message || 'Order finalization failed');
    }
    
    return response;
  } catch (error) {
    console.error('Error finalizing eSIM order:', error);
    
    let errorMessage = 'Order processing failed. Please try again.';
    
    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;
      
      if (data && data.message) {
        errorMessage = data.message;
      } else if (status === 400) {
        errorMessage = 'Invalid order data. Please check your information and try again.';
      } else if (status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (status === 403) {
        errorMessage = 'Access denied. Please contact support.';
      } else if (status === 404) {
        errorMessage = 'Order service not found. Please contact support.';
      } else if (status === 409) {
        errorMessage = 'Order conflict. This order may have already been processed.';
      } else if (status === 422) {
        errorMessage = 'Order validation failed. Please check your details and try again.';
      } else if (status === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (status >= 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else {
        errorMessage = `Order processing failed (${status}). Please try again.`;
      }
    } else if (error.request) {
      // Network error - no response received
      errorMessage = 'Network error. Please check your internet connection and try again.';
    } else if (error.message) {
      // Other error with message
      errorMessage = error.message;
    }
    
    // Create a new error with the specific message
    const apiError = new Error(errorMessage);
    apiError.originalError = error;
    apiError.response = error.response;
    throw apiError;
  }
};
/**
 * Create a payment intent only (no payment method). Used for Google Pay flow:
 * 1) Call this first → get intentId + clientSecret
 * 2) Open Google Pay sheet
 * 3) Confirm PaymentIntent on frontend with stripe.confirmCardPayment(clientSecret, { payment_method: { card: { token } } })
 * 4) Only if payment succeeded, call finalizeSimOrder with buildGooglePayFinalizePayload (payment_intent_id, items, name, email, currency)
 *
 * @param {Object} params - { amount, currency, items, name?, email? }
 * @returns {Promise<{ intentId: string, clientSecret: string }>}
 */
export const createIntent = async (params) => {
  try {
    const { amount, currency, items, name, email } = params;
    const payload = {
      amount: Number(amount),
      currency: (currency || 'USD').toUpperCase(),
      items: items || [],
    };
    if (name) payload.name = name;
    if (email) payload.email = email;

    const response = await postApi('/api/create-intent', payload);

    if (response && response.status === false) {
      throw new Error(response.message || 'Payment intent creation failed');
    }
    const data = response?.data ?? response;
    if (data && data.status === false) {
      throw new Error(data.message || 'Payment intent creation failed');
    }

    const intentId =
      data?.intentId ||
      data?.payment_intent_id ||
      data?.paymentIntentId;
    const clientSecret =
      data?.clientSecret ||
      data?.client_secret;

    if (!intentId || !clientSecret) {
      throw new Error('Invalid create-intent response: missing intentId or clientSecret');
    }
    return { intentId, clientSecret };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Payment setup failed. Please try again.';
    const apiError = new Error(errorMessage);
    apiError.originalError = error;
    apiError.response = error.response;
    throw apiError;
  }
};

/**
 * Build finalize-sim payload for wallet payments (Apple Pay, Google Pay).
 * Same structure: payment_intent_id, items, name, email, currency.
 * Backend only finalizes the order; payment is already confirmed on the frontend.
 */
export const buildGooglePayFinalizePayload = (params) => ({
  payment_intent_id: params.payment_intent_id,
  items: params.items || [],
  name: params.name || '',
  email: params.email || '',
  currency: params.currency || 'USD',
});

export const getIntent = async (orderData) => {
  try {
    const response = await postApi('/api/create-intent', orderData);
    
    // Check if the API response indicates an error
    if (response && response.data && response.data.status === false) {
      throw new Error(response.data.message || 'Payment intent creation failed');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating payment intent:', error);

    let errorMessage = 'Payment setup failed. Please try again.';
    
    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;
      
      if (data && data.message) {
        errorMessage = data.message;
      } else if (status === 400) {
        errorMessage = 'Invalid payment data. Please check your information and try again.';
      } else if (status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (status === 403) {
        errorMessage = 'Access denied. Please contact support.';
      } else if (status === 404) {
        errorMessage = 'Payment service not found. Please contact support.';
      } else if (status === 422) {
        errorMessage = 'Payment validation failed. Please check your details and try again.';
      } else if (status === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (status >= 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else {
        errorMessage = `Payment setup failed (${status}). Please try again.`;
      }
    } else if (error.request) {
      // Network error - no response received
      errorMessage = 'Network error. Please check your internet connection and try again.';
    } else if (error.message) {
      // Other error with message
      errorMessage = error.message;
    }

    // Create a new error with the specific message
    const apiError = new Error(errorMessage);
    apiError.originalError = error;
    apiError.response = error.response;
    throw apiError;
  }
};
